import { useState } from 'react';
import { Receipt } from 'lucide-react';
import Table from '@/components/table/components/Table.tsx';
import EmptyTable from '@/components/table/components/EmptyTable.tsx';
import financialTransactionsApi from '@/features/dashboard/financialTransactions/api/financialTransactionsApi.ts';
import { financialTransactionsApiQueryKeys } from '@/features/dashboard/financialTransactions/api/financialTransactionsApi.queryKeys.ts';
import { RowContextMenuItemParams } from '@/components/table/components/RowContextMenu.tsx';
import { FinancialTransactionListDto } from '@/features/dashboard/financialTransactions/types/FinancialTransactionListDto.ts';
import EditFinancialTransactionModal from '@/features/dashboard/financialTransactions/components/EditFinancialTransactionModal.tsx';
import useDeleteFinancialTransaction from '@/features/dashboard/financialTransactions/hooks/useDeleteFinancialTransaction.ts';
import { confirmDialogUseHook } from '@/components/ui/ConfirmDialog.useHook.ts';
import { TaggableType } from '@/features/dashboard/tags/types/TaggableType.ts';
import { financesRoute } from '@/features/dashboard/finances/routes/financesRoute.ts';

interface EditTarget {
  transactionId: string;
  focusAllocations: boolean;
}

const FinancialTransactionsTable = () => {
  const search = financesRoute.useSearch();
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const deleteTransaction = useDeleteFinancialTransaction();
  const { confirmDeleteWithCallback } = confirmDialogUseHook();

  const rowContextMenuItems: RowContextMenuItemParams<FinancialTransactionListDto>[] =
    [
      {
        label: 'Edytuj',
        action: (row) =>
          setEditTarget({ transactionId: row.id, focusAllocations: false }),
      },
      {
        label: 'Rozdysponuj resztę',
        // Pozycji nie ma, gdy nie ma reszty: wyszarzona mówiłaby, że coś zostało.
        visible: (row) => (row.unallocatedValue?.amount ?? 0) !== 0,
        action: (row) =>
          setEditTarget({ transactionId: row.id, focusAllocations: true }),
      },
      {
        label: 'Usuń',
        variant: 'destructive',
        action: (row) => {
          confirmDeleteWithCallback({
            title: 'Usuń transakcję',
            description: 'Czy na pewno chcesz usunąć tę transakcję?',
            confirmText: 'Usuń',
            onConfirm: () => deleteTransaction.mutateAsync(row.id),
          });
        },
      },
    ];

  return (
    <>
      <Table
        listSearch={search}
        queryKey={financialTransactionsApiQueryKeys.all()}
        fetchData={financialTransactionsApi.getAll}
        fetchTableDef={financialTransactionsApi.getTableDefinition}
        rowContextMenuItems={rowContextMenuItems}
        tagTargetType={TaggableType.TRANSACTION}
        emptyDataComponent={
          <EmptyTable
            title="Brak transakcji"
            description="Nie ma jeszcze żadnych zarejestrowanych transakcji finansowych."
            icon={<Receipt />}
          />
        }
      />

      {editTarget && (
        <EditFinancialTransactionModal
          transactionId={editTarget.transactionId}
          focusAllocations={editTarget.focusAllocations}
          onClose={() => setEditTarget(null)}
        />
      )}
    </>
  );
};

export default FinancialTransactionsTable;
